import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { SERVICES_FR, SERVICES_EN } from '@/data/siteData';
import { Sparkles, Home, Key, Hammer, ClipboardList, UserCheck, ArrowRight } from 'lucide-react';

const iconMap = {
  sparkles: Sparkles,
  home: Home,
  key: Key,
  hammer: Hammer,
  clipboard: ClipboardList,
  concierge: UserCheck,
};

const ServiceCard = ({ service, prefix }) => {
  const { t } = useLanguage();
  const IconComponent = iconMap[service.icon] || Sparkles;

  return (
    <div 
      className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-100 hover:border-[#D4AF37]/30"
      data-testid={`service-card-${service.id}`}
    >
      {/* Icon */}
      <div className="w-14 h-14 bg-[#1a365d] rounded-xl flex items-center justify-center mb-6 group-hover:bg-gradient-to-br group-hover:from-[#D4AF37] group-hover:to-[#C9A227] transition-all duration-300">
        <IconComponent className="w-7 h-7 text-[#D4AF37] group-hover:text-white transition-colors duration-300" />
      </div>

      {/* Title */}
      <h3 className="text-xl font-bold text-[#1a365d] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        {service.shortTitle}
      </h3>

      {/* Description */}
      <p className="text-slate-600 mb-6 leading-relaxed">
        {service.longDescription}
      </p>

      {/* Features */}
      <ul className="space-y-2 mb-6">
        {service.features.map((feature, index) => (
          <li key={index} className="flex items-center gap-2 text-slate-600 text-sm">
            <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
            {feature}
          </li>
        ))}
      </ul>

      {/* Link */}
      <Link
        to={`${prefix}/services/${service.slug}`}
        className="inline-flex items-center gap-2 text-[#D4AF37] font-semibold hover:text-[#B8960C] transition-colors group-hover:gap-3"
      >
        {t('En savoir plus', 'Learn more')}
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
};

const ServicesSection = ({ showAll = true, limit = 3 }) => {
  const { lang, t } = useLanguage();
  const services = lang === 'en' ? SERVICES_EN : SERVICES_FR;
  const prefix = lang === 'en' ? '/en' : '';
  const displayedServices = showAll ? services : services.slice(0, limit);

  return (
    <section className="py-20 bg-[#faf8f5]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-4">
            {t('Nos Prestations', 'Our Services')}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1a365d] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('Services Premium', 'Premium Services')}
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            {t(
              'Des Solutions Complètes pour Votre Propriété',
              'Complete Solutions for Your Property'
            )}
          </p>
          {/* Gold underline */}
          <div className="w-24 h-1 bg-gradient-to-r from-[#D4AF37] to-[#C9A227] mx-auto mt-6 rounded-full" />
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedServices.map((service) => (
            <ServiceCard key={service.id} service={service} prefix={prefix} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
